import { useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { useAuth } from "@/components/feature/AuthProvider";

export default function LoginModal() {
  const { loginOpen, loginMode, closeLogin, login } = useAuth();
  const [riNo, setRiNo] = useState("");
  const [birth4, setBirth4] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needBirth = loginMode === "ri_birth";

  const reset = () => {
    setRiNo("");
    setBirth4("");
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    closeLogin();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const value = riNo.trim();
    if (!value) {
      setError("RI번호를 입력해 주세요.");
      return;
    }
    const birth = birth4.trim();
    if (needBirth && birth.length !== 4) {
      setError("생년월일 4자리(MMDD)를 입력해 주세요.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await login(value, needBirth ? birth : undefined);
    } catch (err) {
      setError((err as Error).message || "로그인에 실패했습니다.");
      setSubmitting(false);
    }
  };

  return (
    <Modal open={loginOpen} onClose={handleClose} labelledBy="login-title">
      <form onSubmit={handleSubmit} className="p-6 md:p-7">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-500 text-primary-950">
              <i className="ri-shield-user-line text-xl" aria-hidden="true"></i>
            </span>
            <h2
              id="login-title"
              className="font-heading text-lg font-bold text-foreground-950"
            >
              회원 확인
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="닫기"
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-foreground-600">
          회원님의 <strong className="text-foreground-900">RI번호</strong>를 입력해
          주세요.
          <br />
          별도의 가입이나 비밀번호는 없습니다.
        </p>

        <label htmlFor="loginRiNo" className="sr-only">
          RI번호
        </label>
        <input
          id="loginRiNo"
          name="ri_no"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="RI번호를 입력해 주세요"
          value={riNo}
          onChange={(e) => setRiNo(e.target.value)}
          className="mt-5 w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-center font-heading text-lg tracking-widest text-foreground-950 placeholder:text-sm placeholder:tracking-normal placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />

        {needBirth && (
          <>
            <label
              htmlFor="loginBirth"
              className="mt-4 block text-sm font-medium text-foreground-700"
            >
              생년월일 4자리 (MMDD)
            </label>
            <input
              id="loginBirth"
              name="birth4"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={4}
              placeholder="예: 0315"
              value={birth4}
              onChange={(e) => setBirth4(e.target.value.replace(/[^0-9]/g, ""))}
              className="mt-1.5 w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-center font-heading text-lg tracking-widest text-foreground-950 placeholder:text-sm placeholder:tracking-normal placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm font-medium text-accent-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 whitespace-nowrap rounded-md bg-secondary-500 px-5 py-3 text-sm font-bold text-primary-950 hover:bg-secondary-600 disabled:opacity-60 transition-colors"
          >
            {submitting ? "확인 중…" : "로그인 하기"}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
          >
            취소
          </button>
        </div>

        <p className="mt-4 text-center text-xs text-foreground-500">
          RI번호를 모르시면 클럽 총무에게 문의해 주세요.
        </p>
      </form>
    </Modal>
  );
}